import {
  type DefaultError,
  type InfiniteData,
  useInfiniteQuery,
  type QueryKey,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";
import {
  useScopedMode,
  useWhenOff,
  type ScopedQueryMode,
  type ScopedQueryOptions,
} from "@/hooks/use-scoped-query";
import type { QueryScope } from "@/queries/scope";

export interface ScopedInfiniteQuery<TData, TError = DefaultError> {
  readonly mode: ScopedQueryMode;
  /** Live in `"cache"` mode; otherwise a disabled query (pending, no pages). */
  readonly query: UseInfiniteQueryResult<TData, TError>;
}

/**
 * `useScopedQuery` for a cursor-paged list (contract §9): the same three modes,
 * the same `enabled`, `flag` and `whenOff`, with `useInfiniteQuery` underneath. A paged
 * domain hook (the teams page, the members table) is then the page definition
 * from `app/queries`, the store's paged read for `"store"` mode, and the rows
 * from whichever source is live:
 *
 *     const { mode, query } = useScopedInfiniteQuery(
 *       slug,
 *       (scope) => teamsPageQuery(workspaceTeamService, scope, params),
 *       { whenOff: () => void workspaceTeams.loadTeamsPage(slug, storeQuery) },
 *     );
 *     const rows = mode === "cache" ? pagesToRows(query.data?.pages ?? []) : store.pagedTeams;
 *
 * With the flag on it is a real observer of the page set: a writer's
 * `invalidateQueries` or a cache hint re-reads every page it holds, and
 * `query.fetchNextPage` appends the next one. In `"store"` mode the caller
 * loads more pages through the store, as before.
 *
 * `makeQuery` is called on every render, also when the read is off (with a
 * placeholder principal), so it must be a pure options builder. Reads the flag
 * and the signed-in user from stores, so the calling component must be an
 * `observer`.
 */
export function useScopedInfiniteQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  tenant: string | undefined,
  makeQuery: (
    scope: QueryScope,
  ) => UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
  { enabled = true, whenOff, flag = null }: ScopedQueryOptions = {},
): ScopedInfiniteQuery<TData, TError> {
  const { mode, scope } = useScopedMode(tenant, flag);
  const options = makeQuery(scope);
  const query = useInfiniteQuery({
    ...options,
    enabled: mode === "cache" && enabled ? (options.enabled ?? true) : false,
  });
  useWhenOff(mode, tenant, options.queryKey, { enabled, whenOff });

  return { mode, query };
}
